// Timezone fixed at Brazil (UTC-3) for the whole D'Pots app.
export const TZ = 'America/Sao_Paulo';

/**
 * Format an ISO date string (YYYY-MM-DD or YYYY-MM-DD HH:MM:SS) as DD/MM/YYYY
 * WITHOUT any timezone conversion — the date part is preserved as-is.
 *
 * This is essential because `new Date("2026-06-13")` is interpreted as UTC
 * midnight by JavaScript, which would render as "12/06" in any timezone
 * west of UTC (including Brazil UTC-3). We avoid that by parsing the
 * components manually.
 */
export function formatDateBR(s) {
  if (!s) return '';
  if (s instanceof Date) return s.toLocaleDateString('pt-BR', { timeZone: TZ });
  const str = String(s);
  // Match YYYY-MM-DD at the start (possibly followed by time)
  const m = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  const d = new Date(str);
  if (isNaN(d)) return str;
  return d.toLocaleDateString('pt-BR', { timeZone: TZ });
}

export function formatDateTimeBR(s) {
  if (!s) return '';
  const d = typeof s === 'string' ? new Date(s.replace(' ', 'T') + (s.includes('T') || s.endsWith('Z') ? '' : 'Z')) : s;
  if (isNaN(d)) return String(s);
  return d.toLocaleString('pt-BR', { timeZone: TZ });
}

export function formatBRL(value) {
  const v = Number(value || 0);
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/**
 * Parse a Brazilian-formatted money string into a Number.
 * Rules:
 *  - If the input contains a comma, the comma is the decimal separator and any
 *    periods are thousand separators: "1.500,03" -> 1500.03
 *  - If only a period is present (US-style), it is treated as the decimal
 *    separator: "1500.03" -> 1500.03
 *  - Leading/trailing whitespace and "R$" prefixes are ignored.
 *  - Empty/invalid -> 0
 */
export function parseBRMoney(s) {
  if (typeof s === 'number') return isFinite(s) ? s : 0;
  if (s == null) return 0;
  let str = String(s).trim().replace(/^R\$\s*/i, '');
  if (!str) return 0;
  const negative = str.startsWith('-');
  if (negative) str = str.slice(1);
  if (str.includes(',')) {
    str = str.replace(/\./g, '').replace(',', '.');
  }
  const n = parseFloat(str);
  if (!isFinite(n)) return 0;
  return negative ? -n : n;
}

/** Format a number as "1.500,03" (without currency symbol). */
export function formatBRNumber(value, { decimals = 2 } = {}) {
  const v = Number(value);
  if (!isFinite(v)) return '';
  return v.toLocaleString('pt-BR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

/** Today's date (Brazil) as YYYY-MM-DD. */
export function todayBR() {
  const fmt = new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' });
  return fmt.format(new Date()); // en-CA gives YYYY-MM-DD
}

/** Current year-month (Brazil) as YYYY-MM. */
export function currentMonthRef() {
  return todayBR().slice(0, 7);
}
